import { AxiosResponse } from 'axios';

import API from '../Api';
import { ContactInfo } from '../types/contactInfo';
import { Contract, ContractBasicData, ContractManagementFormObject } from '../types/contract';
import { getUserToken, logUserOut } from '../utils/login';

export const fetchContractDetails = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  endpoint: string,
  contractIndex: number,
  simple: number,
): Promise<(Contract | undefined)[]> => {
  const { bearer, content } = getUserToken();
  const sessionId = localStorage.getItem('sessionId');

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  try {
    const contracts: AxiosResponse<Contract[]> = await API({
      method: 'post',
      url: `/v1/contracts/${dataOwnerCustomerId}/${actingCustomerId}/${contractIndex}/${simple}?userToken=${sessionId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return error.response;
      }
    });

    const contractData = contracts.data.map((dataPoint) => {
      dataPoint.endpoint = endpoint;

      return dataPoint;
    });

    return contractData;
  } catch {}

  return [];
};

export const postContractManagement = async (
  contractObject: ContractManagementFormObject,
  useCase: string,
  dataOwnerCustomerId: number,
  actingCustomerId: number,
): Promise<XMLHttpRequest> => {
  const { bearer, content } = getUserToken();

  const response: AxiosResponse<string> = await API({
    method: 'post',
    url: `/v1/contractManagement/${useCase}/${dataOwnerCustomerId}/${actingCustomerId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      contractObject,
      content,
    },
  });

  return response.request;
};

export const postContractManagementSalesForce = async (
  contactInfo: ContactInfo,
  dataOwnerCustomerId: number,
  actingCustomerId: number,
): Promise<XMLHttpRequest> => {
  const { bearer, content } = getUserToken();

  const response: AxiosResponse<string> = await API({
    method: 'post',
    url: `/v1/contractManagementSalesForce/${dataOwnerCustomerId}/${actingCustomerId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      contactInfo,
      content,
    },
  });

  return response.request;
};

export const putPaymentType = async (
  dataOwnerCustomerId: number,
  ledgerNo: number,
  paymentTypeNo: number,
  bankId?: number,
  email?: string,
): Promise<XMLHttpRequest> => {
  const { bearer, content } = getUserToken();
  const response: AxiosResponse<string> = await API({
    method: 'put',
    url: `/v1/paymentType/${dataOwnerCustomerId}/${ledgerNo}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      paymentTypeNo: paymentTypeNo,
      bankId: bankId,
      email: email,
      content: `${content}`,
    },
  });

  return response.request;
};

type ProductChangeRequestBody = contactMethodRequestBody;

type contactMethodRequestBody = {
  newContactMethod: string;
};

export const postProductChange = async (
  productName: string,
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  contractNo: number,
  postBody?: ProductChangeRequestBody,
): Promise<XMLHttpRequest> => {
  const { bearer } = getUserToken();

  const response: AxiosResponse<string> = await API({
    method: 'post',
    url: `/v2/productChange/${productName}/${dataOwnerCustomerId}/${actingCustomerId}/${contractNo}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: postBody ? { newContactMethod: postBody.newContactMethod } : {},
  });

  return response.request;
};

export const fetchContractBasicData = async (dataOwnerCustomerId: number): Promise<ContractBasicData[] | null> => {
  const { bearer } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  try {
    const basicDataRequest: AxiosResponse<ContractBasicData[] | null> = await API({
      method: 'get',
      url: `/v1/contractBasicData/${dataOwnerCustomerId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        if (error.response.status === 500) {
          return null;
        }
        return error.response;
      }
    });

    const { data } = basicDataRequest;

    return data;
  } catch {}

  return null;
};

export const fetchContract = async (dataOwnerCustomerId: number, actingCustomerId: number, contractNo: string): Promise<Contract | false> => {
  const { bearer } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  try {
    const contractRequest: AxiosResponse<Contract> = await API({
      method: 'get',
      url: `/v1/contract/${dataOwnerCustomerId}/${actingCustomerId}/${contractNo}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return error.response;
      }
    });

    const { data } = contractRequest;

    return data;
  } catch {}

  return false;
};
